import {
    Component,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    HostListener,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ConnectedPosition, OverlayModule } from "@angular/cdk/overlay";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { filterOrganizationTree } from "src/app/_helpers/org-tree-helper";
import { trackById } from "src/app/shared/utils/track-by.util";

@Component({
    standalone: true,
    selector: "app-org-tree-select",
    imports: [CommonModule, FormsModule, TranslateModule, OverlayModule],
    templateUrl: "./org-tree-select.component.html",
    styleUrls: ["./org-tree-select.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrgTreeSelectComponent {
    @Input() nodes: OrganizationLevel[] = [];
    @Input() selectedId: string | null = null;
    @Input() placeholder = "All Org Levels";
    @Input() hideNullOption = false;
    @Input() disabled = false;
    @Input() set disabledIds(ids: string[] | null | undefined) {
        this.disabledIdSet = new Set(ids ?? []);
        this.cdr.markForCheck();
    }
    @Output() selectionChange = new EventEmitter<string | null>();

    isOpen = false;
    searchText = "";
    protected disabledIdSet = new Set<string>();
    readonly trackByOrgId = trackById;

    readonly overlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
    ];

    constructor(
        private elementRef: ElementRef,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
    ) {}

    @HostListener("document:click", ["$event"])
    onDocumentClick(event: Event): void {
        if (!this.isOpen) return;
        const target = event.target as Node;
        if (this.elementRef.nativeElement.contains(target)) return;
        const insidePanel = (target as Element)?.closest?.('.org-tree-overlay-panel');
        if (insidePanel) return;
        this.isOpen = false;
        this.cdr.markForCheck();
    }

    closePanel(): void {
        this.isOpen = false;
        this.searchText = "";
        this.cdr.markForCheck();
    }

    toggle(): void {
        if (this.disabled) return;
        this.isOpen = !this.isOpen;
        if (!this.isOpen) {
            this.searchText = "";
        }
        this.cdr.markForCheck();
    }

    select(node: OrganizationLevel | null): void {
        if (this.disabled) return;
        if (node && this.disabledIdSet.has(node.id)) return;
        this.selectedId = node?.id ?? null;
        this.isOpen = false;
        this.searchText = "";
        this.selectionChange.emit(this.selectedId);
        this.cdr.markForCheck();
    }

    reset(): void {
        this.selectedId = null;
        this.isOpen = false;
        this.searchText = "";
        this.cdr.markForCheck();
    }

    private filterCache?: { key: string; nodes: OrganizationLevel[]; result: OrganizationLevel[] };

    get filteredNodes(): OrganizationLevel[] {
        const key = this.searchText.trim().toLowerCase();
        if (!key) return this.nodes;
        if (this.filterCache?.key !== key || this.filterCache.nodes !== this.nodes) {
            this.filterCache = { key, nodes: this.nodes, result: filterOrganizationTree(this.nodes, key) };
        }
        return this.filterCache.result;
    }

    get displayLabel(): string {
        if (this.selectedId == null)
            return this.translate.instant(this.placeholder);
        return (
            this.findName(this.nodes, this.selectedId) ??
            this.translate.instant(this.placeholder)
        );
    }

    private findName(nodes: OrganizationLevel[], id: string): string | null {
        for (const node of nodes) {
            if (node.id === id) return node.name;
            if (node.children?.length) {
                const found = this.findName(node.children, id);
                if (found) return found;
            }
        }
        return null;
    }
}
